import { GroupInstance } from "../modelInstance/GroupInstance";
import { EaseInOutEasing } from "./Animation";
import { NumberAnimation } from "./Animation";
import { IStateMachine, StateMachineAction, StateMachineEvent } from "./StateMachine";

const DoorActions: StateMachineAction[] = [
    {
        type: "open",
        name: "Open",
    },
    {
        type: "close",
        name: "Close",
    },
];

type DoorState = "open" | "closed" | "opening" | "closing";

export class DoorStateMachine implements IStateMachine {
    name: string = "Door";
    #group: GroupInstance;
    #openDoorAngle: number;
    #closedDoorAngle: number;
    #state: DoorState;
    #availableActions: ReadonlyArray<StateMachineAction>;
    #eventListeners: Map<StateMachineEvent, ((event: StateMachineEvent) => void)[]>;

    get state(): DoorState {
        return this.#state;
    }

    constructor(group: GroupInstance, openDoorAngle: number, closedDoorAngle: number) {
        this.#group = group;
        this.#availableActions = [DoorActions[0]];
        this.#openDoorAngle = openDoorAngle;
        this.#closedDoorAngle = closedDoorAngle;
        this.#state = "closed";
        this.#eventListeners = new Map();
    }

    startOpening(): void {
        if (this.#state !== "closed") {
            return;
        }

        this.#state = "opening";
        this.#availableActions = [];
        this.triggerEvent("availableActionsChange");
        const animation = new NumberAnimation(
            this.#closedDoorAngle,
            this.#openDoorAngle,
            500,
            EaseInOutEasing
        );
        animation.start(
            (value) => {
                this.#group.rotation.y = value;
                this.triggerEvent("propertyChange");
            },
            () => {
                this.finishOpening();
            }
        );
    }

    finishOpening(): void {
        if (this.#state !== "opening") {
            return;
        }

        this.#availableActions = DoorActions.filter((action) => action.type !== "open");
        this.#state = "open";
        this.triggerEvent("propertyChange");
        this.triggerEvent("availableActionsChange");
    }

    startClosing(): void {
        if (this.#state !== "open") {
            return;
        }

        this.#state = "closing";
        this.#availableActions = [];
        this.triggerEvent("availableActionsChange");
        const animation = new NumberAnimation(
            this.#openDoorAngle,
            this.#closedDoorAngle,
            500,
            EaseInOutEasing
        );
        animation.start(
            (value) => {
                this.#group.rotation.y = value;
                this.triggerEvent("propertyChange");
            },
            () => {
                this.finishClosing();
            }
        );
    }

    finishClosing(): void {
        if (this.#state !== "closing") {
            return;
        }

        this.#availableActions = DoorActions.filter((action) => action.type !== "close");
        this.#state = "closed";
        this.triggerEvent("propertyChange");
        this.triggerEvent("availableActionsChange");
    }

    getAvailableActions(): ReadonlyArray<StateMachineAction> {
        return this.#availableActions;
    }

    triggerAction(action: StateMachineAction): void {
        if (action.type === "open" && this.#availableActions.includes(action)) {
            this.startOpening();
        } else if (action.type === "close" && this.#availableActions.includes(action)) {
            this.startClosing();
        }
    }

    on(event: StateMachineEvent, callback: (event: StateMachineEvent) => void): void {
        if (!this.#eventListeners.has(event)) {
            this.#eventListeners.set(event, []);
        }

        this.#eventListeners.get(event)?.push(callback);
    }

    off(event: StateMachineEvent, callback: (event: StateMachineEvent) => void): void {
        const listeners = this.#eventListeners.get(event);
        if (listeners) {
            listeners.splice(listeners.indexOf(callback), 1);
        }
    }

    triggerEvent(event: StateMachineEvent): void {
        const listeners = this.#eventListeners.get(event);
        if (listeners) {
            listeners.forEach((callback) => callback(event));
        }
    }
}
